import { X } from "@phosphor-icons/react"
import { createFileRoute } from "@tanstack/react-router"
import { useEffect, useState } from "react"
import { z } from "zod"
import CollectionDetailsCard from "~/components/customer-success/impact/CollectionDetailsCard"
import ForecastCard from "~/components/customer-success/impact/ForecastCard"
import InteractionsCard from "~/components/customer-success/impact/InteractionsCard"
import PageVisitsCard from "~/components/customer-success/impact/PageVisitsCard"
import PurchaseOrdersCard from "~/components/customer-success/impact/PurchaseOrdersCard"
import { OverviewPage } from "~/components/design-system/pages"
import { Alert } from "~/components/reusable/Alert"
import { Label } from "~/components/reusable/Label"
import Select from "~/components/reusable/Select"
import { useSafeLocalStorage } from "~/hooks/useSafeLocalStorage"
import { api } from "~/utils/api"

export const Route = createFileRoute("/_auth/internal/customer-success/_layout/impact")({
  component: RouteComponent,
})

const options: {
  label: string
  value: "month" | "quarter"
}[] = [
  { label: "month", value: "month" },
  { label: "quarter", value: "quarter" },
]

function RouteComponent() {
  const [showAlert, setShowAlert] = useSafeLocalStorage("impact-alert", true, z.boolean(), true)
  const { data: companies } = api.company.getAll.useQuery()
  const { data: activeCompany } = api.company.getCompany.useQuery()
  const [company, setCompany] = useState<{ label: string; value: string } | null>(null)
  const [dimension, setDimension] = useState<(typeof options)[0]>({
    label: "month",
    value: "month",
  })

  useEffect(() => {
    if (activeCompany) {
      setCompany({
        label: activeCompany.orgId ?? "Select a company",
        value: activeCompany.orgId ?? "",
      })
    }
  }, [activeCompany])

  return (
    <OverviewPage>
      <div className="flex flex-col gap-4">
        <div className="flex flex-row gap-4 justify-between">
          <div className="flex flex-row gap-2">
            <div className="flex flex-col gap-2">
              <Label className="font-bold">Company</Label>
              <Select
                options={companies
                  ?.sort((a, b) => a.orgId.localeCompare(b.orgId))
                  .map((company) => ({
                    label: company.orgId ?? "Unknown Company",
                    value: company.orgId ?? "",
                  }))}
                value={company}
                onChange={(value) => setCompany(value)}
                isClearable
                placeholder="Select a company"
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label className="font-bold">Time Dimension</Label>
              <Select
                options={[
                  { label: "Month", value: "month" },
                  { label: "Quarter", value: "quarter" },
                ]}
                value={dimension}
                onChange={(selected) =>
                  setDimension(
                    options.find((opt) => opt.value === selected?.value) ?? {
                      label: "Month",
                      value: "month",
                    },
                  )
                }
              />
            </div>
          </div>
          {showAlert && (
            <Alert
              variant={"warning"}
              className="max-w-[450px] fixed right-5 top-25 z-50 m-4 bg-base"
            >
              <div className="flex flex-col justify-start gap-2">
                <h2 className="font-bold text-md">External data source</h2>
                <p>
                  Since we are using an external data source for most of these metrics, the loading
                  times can vary and might fail in some cases.
                </p>
                <p className="underline">
                  If data isn&apos;t showing up after more than a minute, try refreshing.
                </p>
                <X
                  onClick={() => setShowAlert(false)}
                  className="cursor-pointer absolute right-2 top-2"
                />
              </div>
            </Alert>
          )}
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col gap-4">
            <PurchaseOrdersCard company={company?.value} timeDimension={dimension.value} />
            <CollectionDetailsCard company={company?.value} />
            <InteractionsCard company={company?.value} timeDimension={dimension.value} />
          </div>
          <div className="flex flex-col gap-4">
            <ForecastCard company={company?.value} timeDimension={dimension.value} />
            <PageVisitsCard company={company?.value} timeDimension={dimension.value} />
          </div>
        </div>
      </div>
    </OverviewPage>
  )
}
